import PropTypes from 'prop-types';
import { Box, Typography, Tooltip } from '@mui/material';
import { Iconify } from 'src/components';
import { CARD_LABEL_SX } from 'src/components/actionCards';

const TOOLTIP_SX = { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 };

const SOURCE_CONFIGS = {
    FACEBOOK: {
        icon: 'eva:facebook-fill',
        color: '#1877f2',
        bgColor: '#e7f3ff',
    },
    GOOGLE: {
        icon: 'eva:google-fill',
        color: '#ea4335',
        bgColor: '#fce8e6',
    },
    WEBSITE: {
        icon: 'eva:globe-2-fill',
        color: '#8b5cf6',
        bgColor: '#ede9fe',
    },
    PHONE: {
        icon: 'eva:phone-fill',
        color: '#10b981',
        bgColor: '#d1fae5',
    },
    REFERRAL: {
        icon: 'eva:person-fill',
        color: '#f59e0b',
        bgColor: '#fef3c7',
    },
    EMAIL: {
        icon: 'eva:email-fill',
        color: '#3b82f6',
        bgColor: '#dbeafe',
    },
    CAMPAIGN: {
        icon: 'eva:speaker-fill',
        color: '#ec4899',
        bgColor: '#fce7f3',
    },
    default: {
        icon: 'eva:globe-2-fill',
        color: '#64748b',
        bgColor: '#f1f5f9',
    },
};

export default function SourceCards({ sources, title }) {
    const formatCurrency = (value) => {
        if (value >= 1000000) {
            return `£${(value / 1000000).toFixed(1)}M`;
        }
        if (value >= 1000) {
            return `£${(value / 1000).toFixed(1)}K`;
        }
        return `£${value}`;
    };

    const getSourceConfig = (type) => {
        const normalizedType = type?.toUpperCase() || '';
        return SOURCE_CONFIGS[normalizedType] || SOURCE_CONFIGS.default;
    };

    return (
        <Box sx={{ mb: 3 }}>
            {title && (
                <Tooltip title="Leads, revenue and conversion rate broken down by lead source." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help', mb: 2 }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '16px',
                }}
            >
                {sources.map((source, index) => {
                    const config = getSourceConfig(source.type);

                    return (
                        <Box
                            key={source.type || index}
                            sx={{
                                bgcolor: '#fff',
                                borderRadius: '16px',
                                p: '20px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            }}
                        >
                            {/* Header */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        bgcolor: config.bgColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: config.color,
                                    }}
                                >
                                    <Iconify icon={config.icon} width={22} height={22} />
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        textTransform: 'capitalize',
                                    }}
                                >
                                    {source.type?.toLowerCase().replace(/_/g, ' ') || 'Unknown'}
                                </Typography>
                            </Box>

                            {/* Stats — label above a dark number (same order as every other card).
                                The source colour lives on the icon tile only, never on numbers. */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                {[
                                    { label: 'Leads', tip: 'Total leads from this source.', value: source.leads?.toLocaleString() || 0 },
                                    { label: 'Revenue', tip: 'Total revenue from leads attributed to this source.', value: formatCurrency(source.revenue || 0) },
                                    { label: 'Conversion', tip: 'Percentage of leads from this source that converted to treatment.', value: `${source.conversionRate || 0}%` },
                                ].map((stat) => (
                                    <Tooltip key={stat.label} title={stat.tip} placement="top" arrow
                                        slotProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                                        <Box sx={{ textAlign: 'center', cursor: 'help' }}>
                                            <Typography sx={{ ...CARD_LABEL_SX, fontSize: '10px', mb: '4px' }}>
                                                {stat.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', lineHeight: 1.1 }}>
                                                {stat.value}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                ))}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {sources.length === 0 && (
                <Box
                    sx={{
                        textAlign: 'center',
                        py: 6,
                        bgcolor: '#fff',
                        borderRadius: '16px',
                    }}
                >
                    <Typography sx={{ color: '#64748b' }}>
                        No source data available
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

SourceCards.propTypes = {
    sources: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string,
            leads: PropTypes.number,
            revenue: PropTypes.number,
            conversionRate: PropTypes.number,
        })
    ).isRequired,
    title: PropTypes.string,
};

SourceCards.defaultProps = {
    title: 'Lead Sources',
};
